import { useEffect, type RefObject } from 'react';
import type L from 'leaflet';
import { useUIStore } from '../../store/uiStore';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { useStructuresStore } from '../../store/structuresStore';
import {
  getLeaflet,
  DEFENSE_PLAN_DRAWING_CLASS,
  DEFENSE_PLAN_HOME_CLASS,
  DEFENSE_PLAN_READOUT_CLASS,
  DEFENSE_PLAN_VERTEX_SHAKE_CLASS,
} from './leafletHelpers';
import { ON_COLOR } from '../../theme';
import {
  formatArea,
  formatLength,
  pathLengthMeters,
  ringAreaSquareMeters,
} from '../../utils/geoMeasure';
import { isEscapeLayerOpen } from '../../utils/escapeStack';
import { DEFENSE_STRATEGIES, PLAN_LINE_WEIGHT } from './defenseStrategies';
import { DEFENSE_PLAN_PANE, ensureDefensePlanPane } from './useDefensePlanLayer.web';
import { finishStroke, removeLastVertex } from '../../utils/planGeometry';
import {
  nearestStructure,
  hasHomeFeature,
  formatStructureAddress,
  NEAREST_STRUCTURE_MAX_METERS,
} from '../../utils/planHomes';
import type { Structure } from '../../types';

// How far from a house pin, in screen pixels, a Home-mode click still picks it.
const HOME_CLICK_REACH_PX = 28;

// How far the pointer may travel between press and release and still count as
// a click rather than a pan, while a tool is armed. Leaflet's own default is
// 3px (Draggable.options.clickTolerance), which is tight enough that an
// ordinary trackpad click drifts past it: the map pans a few pixels and
// Leaflet then suppresses the click (Map._draggableMoved), so the vertex is
// never placed. Glenn reported this on Sept 22 as "I kept grabbing the map
// image when trying to apply a plan step". Under the raised tolerance Leaflet
// does not move the map at all below the threshold, so the click lands and
// nothing nudges; a deliberate drag still pans. Restored on cleanup, so
// panning outside the draw gesture keeps Leaflet's own feel.
const DRAW_CLICK_TOLERANCE_PX = 10;

/** Leaflet's map-drag Draggable, which owns the click/pan threshold. Reached
 *  through a private field because Map.Drag exposes no way to set it after
 *  construction; absent when dragging is disabled, hence the optional. */
type MapDragInternals = { _draggable?: { options: { clickTolerance: number } } };

// The rubber-band segment that tracks the cursor from the last placed
// vertex — a dashed preview of the segment that would be added next, same
// dash convention as fireHistoryColors' casing but purely visual (never
// committed).
const RUBBER_BAND_DASH = '4 4';

// Vertex dots, in screen pixels. The first corner of an area is the close
// target: bigger, and it grows again when the cursor is within
// CLOSE_REACH_PX, the same distance a click on it counts as closing.
const VERTEX_RADIUS_PX = 4;
const FIRST_VERTEX_RADIUS_PX = 6;
const FIRST_VERTEX_ARMED_RADIUS_PX = 8;
const CLOSE_REACH_PX = 10;
// Gap between the cursor and the readout tag, so the tag never covers the
// crosshair.
const READOUT_OFFSET_PX = 16;
// A double-click on the first corner fires a second click right after the
// one that closed the area; ignore it so it doesn't start a new stroke.
const POST_CLOSE_CLICK_GUARD_MS = 400;

/**
 * Hand-rolled draw gesture for one defense-plan strategy at a time (Sept 9
 * 2026 decision — no leaflet-draw/geoman): click to place vertices, a dashed
 * segment previews where the next one would land, double-click or Enter
 * finishes a line. Escape cancels: mid-stroke (a vertex already placed) it
 * throws away just that stroke and leaves the strategy and kind armed for
 * the next one; idle, it clears the tool entirely (see handleKeyDown). Point
 * mode commits a marker on the first click and stays selected so several
 * points can be dropped in a row. Area
 * mode is the line gesture plus a second dashed preview from the cursor back
 * to the first vertex, so the ring it would close reads before it's
 * committed; double-click or Enter closes it the same way a line finishes.
 *
 * One effect keyed on `[tool, isLoaded]`: picking a different strategy or
 * kind mid-line tears the whole thing down through the effect's own cleanup
 * (the temp layers, the listeners, the CSS class) and starts fresh — there
 * is no separate "cancel" path for a tool change, the dependency array is
 * the cancel path.
 */
export function useDefensePlanDraw({
  mapRef,
  isLoaded,
}: {
  mapRef: RefObject<L.Map | null>;
  isLoaded: boolean;
}): void {
  const tool = useUIStore((s) => s.planDrawTool);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded || !tool) return;

    const L = getLeaflet();
    ensureDefensePlanPane(map);
    const def = DEFENSE_STRATEGIES[tool.strategy];
    const container = map.getContainer();
    container.classList.add(DEFENSE_PLAN_DRAWING_CLASS);
    container.classList.toggle(DEFENSE_PLAN_HOME_CLASS, tool.kind === 'home');
    map.doubleClickZoom.disable();
    const drag = (map.dragging as unknown as MapDragInternals | undefined)?._draggable;
    const previousClickTolerance = drag?.options.clickTolerance;
    if (drag) drag.options.clickTolerance = DRAW_CLICK_TOLERANCE_PX;

    // Line/area state. Never touched by the point tool, which commits on
    // the first click and never accumulates a vertex list. Coords are maps,
    // not [lat,lng] tuples — Leaflet's LatLngExpression accepts {lat,lng}
    // literals directly, and this keeps the vertex list in the same shape
    // Firestore stores (see the schema comment on DefensePlanFeatureSchema).
    let vertices: { lat: number; lng: number }[] = [];
    // One non-interactive dot per placed vertex, index-aligned with
    // `vertices`.
    let dots: L.CircleMarker[] = [];
    // Last cursor position on the map, for the rubber band and readout after
    // a click or Backspace changes the vertices without moving the mouse.
    let cursor: L.LatLng | null = null;
    let cursorPoint: L.Point | null = null;
    let closeArmed = false;
    let ignoreClicksUntil = 0;
    let readoutFrame = 0;
    const isStroke = tool.kind === 'line' || tool.kind === 'area';
    const readout = document.createElement('div');
    readout.className = DEFENSE_PLAN_READOUT_CLASS;
    if (isStroke) container.appendChild(readout);
    let solid: L.Polyline | null = null;
    let rubberBand: L.Polyline | null = null;
    // Area only: the dashed segment from the cursor back to the first
    // vertex, previewing the ring double-click/Enter would close.
    let closingBand: L.Polyline | null = null;

    const hideReadout = () => {
      readout.style.display = 'none';
    };

    // Length (line) or acreage (area, once the cursor makes it three points)
    // of the stroke as it would stand if the cursor's spot were the next
    // vertex. Runs at most once per animation frame.
    const paintReadout = () => {
      readoutFrame = 0;
      if (!cursor || !cursorPoint || vertices.length === 0) return hideReadout();
      const path = [...vertices, { lat: cursor.lat, lng: cursor.lng }];
      let text: string;
      if (tool.kind === 'area') {
        if (path.length < 3) return hideReadout();
        text = formatArea(ringAreaSquareMeters(path));
      } else {
        text = formatLength(pathLengthMeters(path));
      }
      if (readout.textContent !== text) readout.textContent = text;
      readout.style.display = 'block';
      readout.style.transform = `translate(${Math.round(cursorPoint.x + READOUT_OFFSET_PX)}px, ${Math.round(cursorPoint.y + READOUT_OFFSET_PX)}px)`;
    };
    const scheduleReadout = () => {
      if (!isStroke || readoutFrame) return;
      readoutFrame = requestAnimationFrame(paintReadout);
    };

    const addDot = (at: { lat: number; lng: number }) => {
      const isCloseTarget = tool.kind === 'area' && dots.length === 0;
      dots.push(
        L.circleMarker(at, {
          pane: DEFENSE_PLAN_PANE,
          radius: isCloseTarget ? FIRST_VERTEX_RADIUS_PX : VERTEX_RADIUS_PX,
          color: ON_COLOR.text,
          weight: 1.5,
          fillColor: def.stroke,
          fillOpacity: 1,
          interactive: false,
        }).addTo(map)
      );
    };

    // Whether the cursor sits over the first corner of an area that has
    // enough corners to close. Screen distance, so it feels the same at any
    // zoom.
    const nearFirstVertex = (point: L.Point): boolean =>
      tool.kind === 'area' &&
      vertices.length >= 3 &&
      map.latLngToContainerPoint(vertices[0]).distanceTo(point) <= CLOSE_REACH_PX;

    const setCloseArmed = (armed: boolean) => {
      if (armed === closeArmed || !dots[0]) return;
      closeArmed = armed;
      dots[0].setRadius(armed ? FIRST_VERTEX_ARMED_RADIUS_PX : FIRST_VERTEX_RADIUS_PX);
    };

    // One shake on the newest dot. Skipped under reduced motion; the panel
    // hint carries the message either way.
    const shakeLastDot = () => {
      const el = dots[dots.length - 1]?.getElement();
      if (!el || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
      el.classList.remove(DEFENSE_PLAN_VERTEX_SHAKE_CLASS);
      void el.getBoundingClientRect();
      el.classList.add(DEFENSE_PLAN_VERTEX_SHAKE_CLASS);
    };

    // Rubber band and closing band from the current vertices to the cursor.
    const updateBands = () => {
      if (!rubberBand || vertices.length === 0 || !cursor) return;
      rubberBand.setLatLngs([vertices[vertices.length - 1], cursor]);
      closingBand?.setLatLngs([cursor, vertices[0]]);
    };

    const ensureLineLayers = () => {
      if (solid) return;
      solid = L.polyline([], {
        pane: DEFENSE_PLAN_PANE,
        color: def.stroke,
        weight: PLAN_LINE_WEIGHT,
        dashArray: def.dashArray,
        interactive: false,
      }).addTo(map);
      rubberBand = L.polyline([], {
        pane: DEFENSE_PLAN_PANE,
        color: def.stroke,
        weight: PLAN_LINE_WEIGHT,
        dashArray: RUBBER_BAND_DASH,
        interactive: false,
      }).addTo(map);
      if (tool.kind === 'area') {
        closingBand = L.polyline([], {
          pane: DEFENSE_PLAN_PANE,
          color: def.stroke,
          weight: PLAN_LINE_WEIGHT,
          dashArray: RUBBER_BAND_DASH,
          interactive: false,
        }).addTo(map);
      }
    };

    // Tears down the in-progress line/area only — the tool itself stays
    // selected so the next shape (or a run of points) can start immediately.
    // Escape is the one gesture that also clears the tool, handled
    // separately below.
    const resetLine = () => {
      dots.forEach((d) => d.remove());
      dots = [];
      closeArmed = false;
      hideReadout();
      solid?.remove();
      rubberBand?.remove();
      closingBand?.remove();
      solid = null;
      rubberBand = null;
      closingBand = null;
      vertices = [];
      useUIStore.getState().setPlanStrokeActive?.(false);
    };

    const commitPoint = (latlng: L.LatLng) => {
      void useDefensePlansStore.getState().addFeature({
        strategy: tool.strategy,
        kind: 'point',
        coords: [{ lat: latlng.lat, lng: latlng.lng }],
      });
    };

    // Home mode: find the loaded structure nearest the click and, on a hit,
    // commit it as a kind:'point' feature carrying structureId + address
    // (see DefensePlanFeatureSchema's comment on what makes a feature a
    // "home" feature). Reads the same in-memory structure lists
    // useMarkerLayer.web.ts's marker click handler does
    // (_structuresByShard + userAddedStructures) rather than fetching —
    // home mode only makes sense for a structure already on screen, which
    // means it's already loaded. Stays armed either way, same as point
    // mode, so several homes can be added in a row.
    const commitHome = (latlng: L.LatLng) => {
      // While homes aren't drawn, the panel's hint says "Zoom in until homes
      // show" (propertyPinsVisible, set by useMarkerLayer.web.ts). Shard
      // data is loaded at wider zooms too, so without this a click at zoom
      // 15 still found a home nobody could see and added it (Sept 25 2026
      // demo walk). Read the same flag the hint reads, so the two agree.
      if (!useUIStore.getState().propertyPinsVisible) return;
      const structuresState = useStructuresStore.getState();
      const loaded: Structure[] = Object.values(structuresState._structuresByShard).flat();
      loaded.push(...structuresState.userAddedStructures);
      // People click the house icon, whose center sits above the house's
      // own point, so the reach has to scale with zoom: a fixed 30 m missed
      // every icon click at the zoom where pins first appear (~4 m a pixel).
      const clickPoint = map.latLngToContainerPoint(latlng);
      const reachMeters = Math.max(
        NEAREST_STRUCTURE_MAX_METERS,
        map.distance(latlng, map.containerPointToLatLng(clickPoint.add([HOME_CLICK_REACH_PX, 0])))
      );
      const match = nearestStructure({ lat: latlng.lat, lng: latlng.lng }, loaded, reachMeters);
      if (!match) {
        useUIStore.getState().flagPlanHomeMiss();
        return;
      }
      const ownPlan = useDefensePlansStore.getState().ownPlan;
      if (ownPlan && hasHomeFeature(ownPlan.features, tool.strategy, match.id)) return;
      void useDefensePlansStore.getState().addFeature({
        strategy: tool.strategy,
        kind: 'point',
        coords: [{ lat: match.centroid.latitude, lng: match.centroid.longitude }],
        structureId: match.id,
        address: formatStructureAddress(match),
      });
    };

    // Shared by dblclick and Enter, for both line and area. A browser
    // dblclick fires two click events before the dblclick itself, so by the
    // time this runs the vertex list already has one or two near-duplicate
    // points stacked on the end — dropTrailingDuplicates has to run before
    // the length check or a real two-vertex line would look committable
    // when it's actually a click-then-click double-tap on the same spot.
    const tryCommitVertices = () => {
      // Never actually reached for 'point'/'home' — both commit on the
      // first click and never accumulate a vertex list — but narrows
      // `tool.kind` to a real DefenseFeatureKind for the addFeature call
      // below, which stores only line/point/area.
      if (tool.kind === 'point' || tool.kind === 'home') return;
      // A double-click on the first corner closes the area on its first
      // click, so the dblclick that follows finds nothing to finish.
      if (vertices.length === 0) return;
      const outcome = finishStroke(tool.kind, vertices);
      if (outcome.status === 'commit') {
        void useDefensePlansStore.getState().addFeature({
          strategy: tool.strategy,
          kind: tool.kind,
          coords: outcome.coords,
        });
        resetLine();
      } else if (outcome.status === 'too-long') {
        resetLine();
      } else {
        // Too few points to make a shape. Keep the stroke open (minus the
        // duplicates a double-click stacked on the end), nudge the last dot
        // and tell the panel to say so.
        while (dots.length > outcome.vertices.length) dots.pop()!.remove();
        vertices = outcome.vertices;
        solid?.setLatLngs(vertices);
        updateBands();
        shakeLastDot();
        useUIStore.getState().flagPlanTooShort();
        scheduleReadout();
      }
    };

    const handleClick = (e: L.LeafletMouseEvent) => {
      if (tool.kind === 'point') {
        commitPoint(e.latlng);
        return;
      }
      if (tool.kind === 'home') {
        commitHome(e.latlng);
        return;
      }
      if (Date.now() < ignoreClicksUntil) return;
      // Clicking the first corner of an area with three or more corners
      // closes it, the usual gesture.
      if (nearFirstVertex(e.containerPoint)) {
        ignoreClicksUntil = Date.now() + POST_CLOSE_CLICK_GUARD_MS;
        tryCommitVertices();
        return;
      }
      cursor = e.latlng;
      cursorPoint = e.containerPoint;
      ensureLineLayers();
      vertices.push({ lat: e.latlng.lat, lng: e.latlng.lng });
      addDot(vertices[vertices.length - 1]);
      useUIStore.getState().setPlanStrokeActive?.(true);
      solid!.setLatLngs(vertices);
      updateBands();
      scheduleReadout();
    };

    const handleMouseMove = (e: L.LeafletMouseEvent) => {
      if (tool.kind === 'point' || tool.kind === 'home') return;
      cursor = e.latlng;
      cursorPoint = e.containerPoint;
      if (vertices.length === 0 || !rubberBand) return;
      updateBands();
      setCloseArmed(nearFirstVertex(e.containerPoint));
      scheduleReadout();
    };

    const handleMouseOut = () => {
      cursor = null;
      cursorPoint = null;
      hideReadout();
    };

    const handleDblClick = () => {
      if (tool.kind === 'point') return;
      tryCommitVertices();
    };

    const isTypingTarget = (target: EventTarget | null): boolean => {
      const el = target as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      if (e.key === 'Escape') {
        // A dialog or sheet above the panel owns this press (utils/
        // escapeStack) — stand aside so it doesn't also cancel the stroke
        // or clear the strategy underneath.
        if (isEscapeLayerOpen()) return;
        e.preventDefault();
        // Mid-stroke (a line/area with at least one vertex placed): Escape
        // cancels just that stroke and leaves the strategy and kind armed,
        // so the next click starts a fresh one of the same kind. Point and
        // home features never accumulate vertices (they commit on the
        // first click — see commitPoint/commitHome), so for those, and for
        // an idle line/area with nothing placed yet, Escape clears the tool
        // the way it always has.
        if (vertices.length > 0) {
          resetLine();
        } else {
          useUIStore.getState().setPlanDrawTool(null);
        }
      } else if (e.key === 'Backspace' && isStroke) {
        // Undo the last placed point. With nothing placed there is nothing
        // to undo, so the key is left alone.
        if (vertices.length === 0 || isEscapeLayerOpen()) return;
        e.preventDefault();
        if (vertices.length === 1) {
          resetLine();
          return;
        }
        vertices = removeLastVertex(vertices);
        dots.pop()?.remove();
        solid?.setLatLngs(vertices);
        setCloseArmed(false);
        if (cursorPoint && nearFirstVertex(cursorPoint)) setCloseArmed(true);
        updateBands();
        scheduleReadout();
      } else if (e.key === 'Enter' && tool.kind !== 'point') {
        e.preventDefault();
        tryCommitVertices();
      }
    };

    map.on('click', handleClick);
    map.on('mousemove', handleMouseMove);
    map.on('dblclick', handleDblClick);
    map.on('mouseout', handleMouseOut);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      map.off('click', handleClick);
      map.off('mousemove', handleMouseMove);
      map.off('dblclick', handleDblClick);
      map.off('mouseout', handleMouseOut);
      if (readoutFrame) cancelAnimationFrame(readoutFrame);
      readout.remove();
      document.removeEventListener('keydown', handleKeyDown);
      resetLine();
      container.classList.remove(DEFENSE_PLAN_DRAWING_CLASS, DEFENSE_PLAN_HOME_CLASS);
      map.doubleClickZoom.enable();
      if (drag && previousClickTolerance !== undefined) {
        drag.options.clickTolerance = previousClickTolerance;
      }
    };
  }, [tool, isLoaded, mapRef]);
}
